import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { TechDataAPI, extractError } from "../lib/api";

const CATEGORIES = [
    "maintenance_schedule", "torque_spec", "fluid_spec",
    "belt_chain", "wiring_circuit", "repair_procedure",
    "service_bulletin", "recall_notice",
];

export default function TechDataNewPage() {
    const nav = useNavigate();
    const [title, setTitle] = useState("");
    const [category, setCategory] = useState("torque_spec");
    const [body, setBody] = useState("");
    const [make, setMake] = useState("");
    const [model, setModel] = useState("");
    const [yearFrom, setYearFrom] = useState("");
    const [yearTo, setYearTo] = useState("");
    const [unitsText, setUnitsText] = useState("");
    const [submitting, setSubmitting] = useState(false);

    const submit = async () => {
        let units = {};
        if (unitsText.trim()) {
            try { units = JSON.parse(unitsText); }
            catch { toast.error("Specs must be valid JSON, e.g. {\"wheel_nut_nm\": 90}"); return; }
        }
        setSubmitting(true);
        try {
            const out = await TechDataAPI.create({
                title, category, body_markdown: body,
                make: make || null, model: model || null,
                year_range_start: yearFrom ? Number(yearFrom) : null,
                year_range_end: yearTo ? Number(yearTo) : null,
                units, is_active: true,
            });
            toast.success("Tech-data entry created");
            nav(`/tech-data/${out.id}`);
        } catch (e) { toast.error(extractError(e)); }
        finally { setSubmitting(false); }
    };

    return (
        <div data-testid="tech-data-new-page" className="space-y-4 max-w-2xl">
            <div className="flex items-center justify-between">
                <h1 className="text-3xl font-bold tracking-tight">New service spec</h1>
                <Button asChild variant="outline"><Link to="/tech-data">Cancel</Link></Button>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Input data-testid="tech-data-new-title" placeholder="Title" value={title} onChange={(e) => setTitle(e.target.value)} className="sm:col-span-2" />
                <Select value={category} onValueChange={setCategory}>
                    <SelectTrigger data-testid="tech-data-new-category"><SelectValue /></SelectTrigger>
                    <SelectContent>
                        {CATEGORIES.map((c) => (
                            <SelectItem key={c} value={c}>{c.replace(/_/g, " ")}</SelectItem>
                        ))}
                    </SelectContent>
                </Select>
                <Input data-testid="tech-data-new-make" placeholder="Make (optional)" value={make} onChange={(e) => setMake(e.target.value)} />
                <Input data-testid="tech-data-new-model" placeholder="Model (optional)" value={model} onChange={(e) => setModel(e.target.value)} />
                <div className="grid grid-cols-2 gap-2">
                    <Input data-testid="tech-data-new-year-from" type="number" placeholder="Year from" value={yearFrom} onChange={(e) => setYearFrom(e.target.value)} />
                    <Input data-testid="tech-data-new-year-to" type="number" placeholder="Year to" value={yearTo} onChange={(e) => setYearTo(e.target.value)} />
                </div>
                <Textarea data-testid="tech-data-new-body" rows={10} placeholder="Body (markdown)" value={body} onChange={(e) => setBody(e.target.value)} className="sm:col-span-2" />
                <Textarea data-testid="tech-data-new-units" rows={3} placeholder='Specs JSON (optional), e.g. {"wheel_nut_nm": 90}' value={unitsText} onChange={(e) => setUnitsText(e.target.value)} className="sm:col-span-2" />
            </div>
            <Button onClick={submit} disabled={submitting || !title || !body} data-testid="tech-data-new-submit">
                {submitting ? "Saving…" : "Save"}
            </Button>
        </div>
    );
}
